import React, { useId } from 'react';
import { More } from '@fabric-ui/icons';
import { Box, IconButton, List, ListItem, ListItemIcon, ListItemText, Paper, Popper, styled } from '@mui/material';
import { itemAttrs } from '../../../utils/itemAttrs';
import { useChartInteractionState } from '../../ChartInteractionProvider';
import { useChartAccessorsContext } from '../../ChartRoot';
import { ColorDot, highlighted, ITEM_HEIGHT, LIST_MAX_HEIGHT, ROW_GAP, StyledLegendLabel } from '../Legend.styles';
import { LegendRenderer } from '../Legend.types';
import { useLegendInteractions } from '../useLegendInteractions';

const computeFirstHiddenIndex = (entry: ResizeObserverEntry) => {
  const isOverflowing = entry.target.scrollHeight > entry.target.clientHeight;
  return isOverflowing
    ? Array.from(entry.target.children).findIndex(child => child.getBoundingClientRect().top > entry.target.getBoundingClientRect().bottom)
    : -1;
};

export const ListLegend: LegendRenderer = ({ data }) => {
  const { getValue, getIdentifier, getColor, getSelected, labelFormatter } = useChartAccessorsContext();
  const getLabel = (d: unknown) => labelFormatter?.(d, { where: 'legend' }) ?? String(getValue(d));

  const { highlightedItem, isPinnable } = useChartInteractionState();
  const { onClick, onPointerEnter, onPointerLeave } = useLegendInteractions();
  const [anchor, setAnchor] = React.useState<HTMLElement | null>(null);
  const [firstHiddenIndex, setFirstHiddenIndex] = React.useState<number>(-1);
  const isOverflowing = firstHiddenIndex > -1;
  const overflowItems = isOverflowing ? data.slice(firstHiddenIndex) : [];
  const uniqueId = useId();

  const handleRef = React.useRef((node: HTMLElement | null) => {
    const handleEntry: ResizeObserverCallback = ([entry]) => setFirstHiddenIndex(computeFirstHiddenIndex(entry));
    const obs = new ResizeObserver(handleEntry);
    if (node) obs.observe(node);
    return () => obs.disconnect();
  });

  return (
    <Box position="relative">
      <HorizontalListContainer aria-label="Chart legend" sx={{ paddingRight: 3 }} ref={handleRef.current}>
        {data.map((d, index) => {
          const id = getIdentifier(d);
          const isHidden = isOverflowing && index >= firstHiddenIndex;
          const isHighlighted = !isHidden && highlightedItem === id;
          const isSelected = !isHidden && (getSelected ? getSelected(d, index) : false);
          const isInteractive = !isHidden && isPinnable;
          const ariaId = `${uniqueId}${id}`;

          return (
            <StyledLegendItem
              key={id}
              {...itemAttrs({
                id,
                index,
                highlighted: isHighlighted,
                selected: isSelected,
                interactive: isInteractive,
              })}
              aria-hidden={isHidden || undefined}
              sx={isHidden ? { visibility: 'hidden', pointerEvents: 'none' } : undefined}
              onPointerEnter={isHidden ? undefined : () => onPointerEnter(d, id, index)}
              onPointerLeave={isHidden ? undefined : onPointerLeave}
              onClick={isHidden ? undefined : event => onClick(event, d, id, index)}
              aria-labelledby={ariaId}>
              <StyledLegendLabel noWrap variant="caption" display="flex" alignItems="center" id={ariaId}>
                <ColorDot color={getColor(d)} />
                {getLabel(d)}
              </StyledLegendLabel>
            </StyledLegendItem>
          );
        })}
      </HorizontalListContainer>
      {isOverflowing && (
        <IconButton
          size="small"
          component="span"
          title="Show More"
          sx={{
            position: 'absolute',
            bottom: 0,
            right: 0,
            marginRight: 0.5,
            marginBottom: 0.25,
          }}
          onPointerEnter={e => setAnchor(e.currentTarget)}
          onPointerLeave={() => setAnchor(null)}>
          <More style={{ width: 16, height: 16, transform: `rotate(90deg)`, color: 'var(--color-content-interactive-tertiary-default)' }} />

          <Popper open={!!anchor} anchorEl={anchor} placement="bottom-start" sx={{ zIndex: t => t.zIndex.modal }}>
            <Paper sx={{ width: 275, maxHeight: 300, overflowY: 'auto' }}>
              <List disablePadding sx={{ backgroundColor: 'var(--color-surface-elevated-10)' }}>
                {overflowItems.map((d, i) => {
                  const index = firstHiddenIndex + i;
                  const id = getIdentifier(d);
                  const isHighlighted = highlightedItem === id;
                  const isSelected = getSelected ? getSelected(d, index) : false;

                  return (
                    <ListItem
                      key={id}
                      disableGutters
                      disablePadding
                      sx={{
                        gap: '6px',
                        paddingLeft: 1,
                        '&[data-interactive]': {
                          cursor: 'pointer',
                        },
                        '&[data-highlighted]': {
                          backgroundColor: 'var(--color-surface-list-hover)',
                        },
                        '&[data-selected]': {
                          backgroundColor: 'var(--color-surface-list-focus)',
                        },
                        '&[data-selected][data-highlighted]': {
                          backgroundColor: 'var(--color-surface-list-focus)',
                        },
                      }}
                      {...itemAttrs({ id, index, highlighted: isHighlighted, selected: isSelected, interactive: isPinnable })}
                      onPointerEnter={() => onPointerEnter(d, id, index)}
                      onPointerLeave={onPointerLeave}
                      onClick={event => onClick(event, d, id, index)}>
                      <ListItemIcon sx={{ minWidth: 'auto' }}>
                        <ColorDot color={getColor(d)} />
                      </ListItemIcon>
                      <ListItemText sx={{ color: 'var(--color-content-base-secondary)' }}>{getLabel(d)}</ListItemText>
                    </ListItem>
                  );
                })}
              </List>
            </Paper>
          </Popper>
        </IconButton>
      )}
    </Box>
  );
};

const StyledLegendItem = styled('li')(() => ({
  // eslint-disable-next-line fabric-ui/css-best-practices
  display: 'flex',
  alignItems: 'center',
  height: ITEM_HEIGHT,
  gap: 6,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  borderRadius: 4,
  padding: '6px 8px',
  ...highlighted(),
}));

const HorizontalListContainer = styled('ul')({
  // eslint-disable-next-line fabric-ui/css-best-practices
  display: 'flex',
  maxHeight: LIST_MAX_HEIGHT,
  rowGap: ROW_GAP,
  columnGap: 4,
  flexWrap: 'wrap',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  padding: 0,
  margin: 0,
  width: '100%',
});
